import React, { useState } from 'react'
import Gap from './Gap'
import FormTextarea from './FormTextarea'
import SubmitButton from './SubmitButton'
import { reviewKeywordApi } from '../api/reviewKeyword'
import toastUtil from '../utils/toast'
import { useSheetDragDismiss } from '../hooks/useSheetDragDismiss'
import SheetHeader from './SheetHeader'

const MAX_LENGTH = 300

interface ReviewKeywordSuggestBottomSheetProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * "키워드 추가"(제안) 바텀시트.
 * 기존 /review-keyword-suggest 페이지를 바텀시트로 대체 — 찾는 키워드가 없을 때 제안.
 */
const ReviewKeywordSuggestBottomSheet: React.FC<
  ReviewKeywordSuggestBottomSheetProps
> = ({ isOpen, onClose }) => {
  const [suggestionText, setSuggestionText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleClose = () => {
    if (isSubmitting) return // 제출 중엔 닫기 방지
    setSuggestionText('')
    onClose()
  }

  const handleSubmit = async () => {
    const trimmedText = suggestionText.trim()

    if (!trimmedText) {
      toastUtil.warning('제안 내용을 입력해주세요')
      return
    }
    if (trimmedText.length > MAX_LENGTH) {
      toastUtil.warning(`제안 내용은 최대 ${MAX_LENGTH}자까지 가능합니다`)
      return
    }

    setIsSubmitting(true)
    try {
      await reviewKeywordApi.suggestKeyword({ suggestionText: trimmedText })
      toastUtil.success('제안이 완료되었습니다')
      setSuggestionText('')
      onClose()
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || '제안 등록 중 오류가 발생했습니다'
      toastUtil.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    if (newValue.length <= MAX_LENGTH) {
      setSuggestionText(newValue)
    }
  }

  const isFormValid = suggestionText.trim().length > 0

  const { handleProps, sheetStyle } = useSheetDragDismiss({
    onClose: handleClose,
    enabled: isOpen,
  })

  if (!isOpen) return null

  return (
    <>
      {/* 오버레이 */}
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-[1060]"
        onClick={handleClose}
      />

      {/* 바텀 시트 컨테이너 */}
      <div className="fixed bottom-0 left-0 right-0 z-[1070] flex justify-center">
        <div className="app-container flex flex-col" style={sheetStyle}>
          <div className="bg-ivoryKey rounded-t-30px shadow-lg overflow-hidden flex flex-col w-full">
            {/* 드래그 핸들 (아래로 끌면 닫힘) */}
            <div className="flex flex-col w-full" {...handleProps}>
              <Gap height={24} />
              <div className="w-40px h-5px bg-d9d9d9 rounded-20px mx-auto"></div>
            </div>

            <Gap height={12} />

            {/* 헤더 (공용) */}
            <SheetHeader title="키워드 추가" />

            <Gap height={20} />

            {/* 입력 */}
            <div className="px-20px">
              <FormTextarea
                name="suggestionText"
                value={suggestionText}
                onChange={handleTextChange}
                placeholder="찾으시는 키워드가 없다면 제안해보세요"
                maxLength={MAX_LENGTH}
              />
            </div>

            <Gap height={16} />

            {/* 완료 버튼 */}
            <div className="px-20px pb-32px">
              <SubmitButton
                text="완료"
                onSubmit={handleSubmit}
                disabled={!isFormValid || isSubmitting}
                loading={isSubmitting}
                onDisabled={handleSubmit}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ReviewKeywordSuggestBottomSheet
